import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { createCase, getCaseById, updateCase } from '../../../services/cases';
import { getMembers } from '../../../services/members';
import { getSettings } from '../../../services/settings';
import { CASE_TYPES } from '../../../utils/constants';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

const CaseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [settings, setSettings] = useState({});
  const [loadingMembers, setLoadingMembers] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const selectedType = watch('type');

  useEffect(() => {
    loadMembers();
    loadSettings();
    if (isEdit) loadCase();
  }, [id]);

  const loadMembers = async () => {
    try {
      const data = await getMembers();
      setMembers(
        data.map((m) => ({
          value: m.id,
          label: `${m.member_number || 'N/A'} - ${m.user?.full_name || m.full_name || 'Unnamed'}`,
        }))
      );
    } catch (err) {
      console.error('Error loading members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadSettings = async () => {
    try {
      const data = await getSettings();
      const amountMap = {};
      data.forEach((setting) => {
        if (setting.key.startsWith('case_type_') && setting.key.endsWith('_amount')) {
          const typeName = setting.key.replace('case_type_', '').replace('_amount', '');
          amountMap[typeName] = setting.value;
        }
      });
      setSettings(amountMap);
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const loadCase = async () => {
    try {
      const data = await getCaseById(id);
      reset(data);
      if (settings[data.type]) {
        setValue('target_amount', settings[data.type]);
      }
    } catch (err) {
      console.error('Error loading case:', err);
    }
  };

  // Auto-populate amount when type changes
  useEffect(() => {
    if (selectedType && settings[selectedType]) {
      setValue('target_amount', settings[selectedType]);
    } else {
      setValue('target_amount', '');
    }
  }, [selectedType, settings, setValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEdit) {
        await updateCase(id, data);
      } else {
        await createCase(data);
      }
      navigate('/dashboard/org-admin/cases');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save case');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/dashboard/org-admin/cases')}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Case' : 'Create New Case'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        {/* Member Select */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">
            Select Member <span className="text-red-500">*</span>
          </label>
          <select
            {...register('member_id', { required: 'Please select a member' })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
              errors.member_id ? 'border-red-500' : 'border-gray-300'
            }`}
            disabled={loadingMembers}
          >
            <option value="">{loadingMembers ? 'Loading members...' : 'Select a member'}</option>
            {members.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          {errors.member_id && (
            <p className="mt-1 text-sm text-red-600">{errors.member_id.message}</p>
          )}
        </div>

        {/* Case Type Select */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">
            Case Type <span className="text-red-500">*</span>
          </label>
          <select
            {...register('type', { required: 'Please select a case type' })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
              errors.type ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select case type</option>
            {CASE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>}
        </div>

        {/* Target Amount */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">
            Target Amount (KES) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            step="0.01"
            {...register('target_amount', {
              required: 'Target amount is required',
              min: { value: 0, message: 'Amount must be greater than 0' },
            })}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none ${
              errors.target_amount ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={selectedType && settings[selectedType] ? `Default: ${settings[selectedType]}` : 'Enter amount'}
          />
          {errors.target_amount && (
            <p className="mt-1 text-sm text-red-600">{errors.target_amount.message}</p>
          )}
        </div>

        {/* Description (Optional) */}
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">Description (Optional)</label>
          <textarea
            {...register('description')}
            rows="3"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            placeholder="Add any additional details..."
          />
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/dashboard/org-admin/cases')}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                {isEdit ? 'Update Case' : 'Create Case'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CaseForm;